// Prequalification funnel — marketing attribution capture.
//
// Phoenix is about to put the Phoenix default prequalification link on every
// paid social advertisement it runs, so "which campaign produced this buyer"
// becomes the question the manager dashboard exists to answer. The
// BuyerLead attribution columns have existed since that module shipped and have
// been null on every row, because nothing ever captured a value to put in them.
//
// ── WHY IT IS CAPTURED AT LANDING AND NOT AT FINALIZE ────────────────────
//
// The funnel is several steps long and the applicant navigates between them.
// Only the FIRST page view carries the ad's query string; by the time they
// reach the finalize step the URL is the funnel's own. So the values are read
// once, on arrival, and held in sessionStorage for the rest of the run.
//
// ── AND WHY IT IS FIRST-WRITE-WINS ───────────────────────────────────────
//
// Re-reading on a later mount would let an internal navigation with no query
// string overwrite a real campaign with nothing, which is worse than never
// having captured it: the row would read as organic traffic and the campaign
// that actually paid for the lead would go uncredited.
//
// ── GOOGLE CLICK IDS: THIS FILE NEVER READ ONE, AND NOW IT NEED NOT ──────
//
// `gclid` is deliberately absent below, and was never here. For a long time
// that was a recorded GAP rather than a decision — Google Ads auto-tagging
// appends a click id and no `utm_*` at all unless a campaign sets them by
// hand, so `hasCampaignSignal` treated an auto-tagged visit as organic and the
// campaign that paid for it went uncredited. `/buy` captured one; this did
// not; `PrequalAttribution.Gclid` on the server said so in as many words.
//
// Phoenix no longer advertises on Google, so the gap closed by the other side
// moving: `/buy` stopped capturing it too, and the two public surfaces now
// agree. The SERVER field and the `BuyerLead.Gclid` column both stay —
// historical leads hold real values and a channel decision can reverse — so
// re-enabling means adding the read back HERE and in `buy.html`, with no
// server change and no migration.
//
// 🔴 If that day comes, note that the two surfaces would need the same rule:
// a click id has to count toward `hasCampaignSignal` (below) or capturing it
// changes nothing, because a visit carrying only a click id returns null
// before the value is ever stored.

import { useEffect } from "react";

const STORAGE_KEY = "phx:prequal:attribution:v1";

/** The wire shape — mirrors `PrequalAttribution` on the server, which does the
 *  authoritative sanitising and length-capping. */
export interface PrequalAttribution {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
  landingPage?: string | null;
  formId?: string | null;
}

/** Generous, and NOT the server's limit. The server is the authority and clips
 *  to its own column widths; this only stops an absurd query string from
 *  filling sessionStorage before it ever gets there. */
const MAX_LEN = 2000;

function clean(value: string | null): string | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length <= MAX_LEN ? trimmed : trimmed.slice(0, MAX_LEN);
}

function isEmpty(a: PrequalAttribution): boolean {
  return !(
    a.utmSource ||
    a.utmMedium ||
    a.utmCampaign ||
    a.utmContent ||
    a.utmTerm ||
    a.landingPage ||
    a.formId
  );
}

/**
 * Read the attribution off a URL. `formId` identifies WHICH funnel form was
 * filled — the buyer and seller funnels are different forms behind the same
 * ads — so it comes from the caller rather than the query string.
 */
export function readAttributionFromUrl(
  href: string,
  formId?: string,
): PrequalAttribution | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const q = url.searchParams;
  const captured: PrequalAttribution = {
    utmSource: clean(q.get("utm_source")),
    utmMedium: clean(q.get("utm_medium")),
    utmCampaign: clean(q.get("utm_campaign")),
    utmContent: clean(q.get("utm_content")),
    utmTerm: clean(q.get("utm_term")),
    // Origin + path only. The query string is where the UTMs already are, and
    // a full href would duplicate them into a second column while dragging any
    // unrelated parameter — an invite token included — along with it.
    landingPage: clean(`${url.origin}${url.pathname}`),
    formId: clean(formId ?? null),
  };

  // A landing page on its own is not attribution: every direct visit has one,
  // and storing it alone would make organic traffic look tracked.
  const hasCampaignSignal = Boolean(
    captured.utmSource ||
      captured.utmMedium ||
      captured.utmCampaign ||
      captured.utmContent ||
      captured.utmTerm,
  );
  if (!hasCampaignSignal) return null;

  return isEmpty(captured) ? null : captured;
}

/** What was captured for this run, if anything. */
export function loadAttribution(): PrequalAttribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PrequalAttribution;
    return parsed && typeof parsed === "object" && !isEmpty(parsed) ? parsed : null;
  } catch {
    // A stale or malformed blob is ignored, never thrown — losing a lead over
    // a marketing parameter would be absurd.
    return null;
  }
}

/**
 * Capture once per run, from the URL the applicant arrived on.
 *
 * FIRST WRITE WINS: a later call with no campaign in the URL leaves an
 * already-captured one alone. Returns whatever is in force afterwards.
 */
export function captureAttribution(
  href: string,
  formId?: string,
): PrequalAttribution | null {
  const existing = loadAttribution();
  if (existing) return existing;

  const captured = readAttributionFromUrl(href, formId);
  if (!captured) return null;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Private-browsing quota, most likely. The value is still returned and
    // still rides this run's finalize; only a resume would lose it.
  }
  return captured;
}

/** Test seam — the funnel itself never clears this. */
export function clearAttribution(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Capture the arriving URL's attribution once, at mount.
 *
 * Called from every funnel ENTRY page — the landing page and both role funnels
 * — because an advertisement may point at any of them. That is safe precisely
 * because capture is first-write-wins: the second and third calls of a run see
 * a URL with no campaign on it and leave the real one alone.
 */
export function usePrequalAttributionCapture(formId?: string): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    captureAttribution(window.location.href, formId);
  }, [formId]);
}
